import { EPS as SHARED_EPS } from './epsilon.js';
/** Whether the shared endpoint between two adjacent spans belongs to at least one of them. */
export function touchesClosed(loSide, hiSide) {
    return loSide.hiInclusive || hiSide.loInclusive;
}
/**
 * Whether two spans share any value at all.
 *
 * The endpoint cases are the reason this is not `a.hi >= b.lo && b.hi >= a.lo`: two spans that meet
 * at a point overlap only if BOTH admit that point.
 */
export function rangesOverlap(a, b) {
    if (a.hi < b.lo || b.hi < a.lo)
        return false;
    if (a.hi === b.lo)
        return a.hiInclusive && b.loInclusive;
    if (b.hi === a.lo)
        return b.hiInclusive && a.loInclusive;
    return true;
}
/**
 * Sorted spans, with everything that genuinely joins merged together.
 *
 * Two spans that merely TOUCH join only when the shared point belongs to one of them — the test
 * that stops `< 5` and `> 5` from becoming "everything". Compared with an epsilon because a bound
 * computed from a formula is float-dirty by construction: `lte {a}+{b}` produces an endpoint that
 * is not exactly the clean decimal a student would type.
 */
export function mergeIntervals(intervals, 
/**
 * The float tolerance for "these two bounds are the same point", INJECTED because the blocks do
 * not agree on it and this refactor must not decide for them.
 *
 * The census found two epsilons six orders of magnitude apart: the shared
 * `16 * Number.EPSILON` (~3.5e-15, a true float-rounding tolerance) and a local `1e-9` defined
 * five times over. It is not cosmetic — it decides whether a value a hair outside an accepted
 * band grades as inside, so the same authored answer could grade differently on numerical than
 * on slider.
 *
 * RESOLVED for slider, which now passes the shared one. Both forms are `k * max(1, |n|)`, so the
 * `max` gives them a FLOOR: below 1 the constant itself becomes the tolerance, and at an exact
 * answer of 5e-9 `1e-9` was accepting roughly +/-20% of the value. Numerical had already moved
 * for exactly that reason; slider was the one other block whose domain is AUTHOR-DEFINED (a log
 * slider from 1e-9 to 1e-6 is legal), so it carried the same exposure and none of the fix. See
 * `slider-epsilon.test.ts`.
 *
 * Angle, compass and gps keep `1e-9` deliberately: their domains are intrinsic and large (0-360
 * degrees, metres), so no value sits where `1e-9` is a meaningful fraction, and tightening them
 * would be a grading change with no bug behind it. They still pass it explicitly, which is why
 * this stays a parameter rather than becoming a constant.
 */
eps = SHARED_EPS) {
    const sorted = [...intervals].sort((a, b) => a.lo - b.lo);
    const merged = [];
    for (const iv of sorted) {
        const last = merged[merged.length - 1];
        const contiguous = !!last && (iv.lo < last.hi - eps(last.hi)
            || (Math.abs(iv.lo - last.hi) <= eps(last.hi) && touchesClosed(last, iv)));
        if (last && contiguous) {
            if (iv.hi > last.hi) {
                last.hi = iv.hi;
                last.hiInclusive = iv.hiInclusive;
            }
            else if (iv.hi === last.hi)
                last.hiInclusive = last.hiInclusive || iv.hiInclusive;
        }
        else {
            merged.push({ ...iv });
        }
    }
    return merged;
}
/**
 * Everything the block accepts, PLUS everything its span-shaped targeted feedback already claims.
 *
 * `spanOf` is the block's own reading of a trigger, and injecting it is the whole point: numerical,
 * slider and angle each parse a different trigger vocabulary, and angle bounds a one-sided trigger
 * to its own 0-360 domain where the others run to infinity. Those differences are real and belong
 * to the blocks; the merging does not.
 *
 * Returning `null` from `spanOf` means "this trigger adds no coverage", which is how every caller
 * excludes an `exact` trigger: a single point never closes a gap in interval terms. That rule is
 * inherited deliberately rather than re-decided here — and it is worth knowing that it OVER-CLAIMS
 * on a discrete domain like slider's, where enough targeted exacts on reachable positions really
 * could exhaust the gaps. Recorded in `project_coverage_algorithm_consolidation_deferred`; not
 * changed here, because changing it changes grading.
 */
export function mergeCoverageIntervals(acceptedRanges, feedbackTriggers, spanOf, eps) {
    const intervals = [...acceptedRanges];
    for (const trigger of feedbackTriggers) {
        const span = spanOf(trigger);
        if (span)
            intervals.push(span);
    }
    return mergeIntervals(intervals, eps);
}
/**
 * Whether merged coverage leaves NOTHING in the domain uncovered — i.e. Default Incorrect can never
 * fire, because the accepted band plus the targeted feedback between them claim every value.
 *
 * This was three copies (numerical, slider, gps), each written as `merged.length === 1` plus a pair
 * of hand-inlined domain comparisons. Expressed as `.some()` the length test is redundant: a span
 * that reaches both domain edges has already absorbed every other span, so it can only be the
 * single remaining one.
 *
 * The domain is where the difference lived - numerical and slider are unbounded, gps starts at 0
 * because it measures a distance. `eps` defaults to NO tolerance, which is what those three had;
 * angle and compass do not use this function at all (see `coverageGaps` for why).
 */
export function coverageSpansDomain(
/**
 * Deliberately typed on `lo`/`hi` alone rather than `BoundedInterval`: reaching a domain EDGE is
 * a question about magnitude, and whether the span happens to admit its own endpoint changes
 * nothing about whether it got there. Asking only for what it reads is also what lets gps and
 * compass, whose interval types carry no inclusive flags, pass their spans straight in instead of
 * decorating them with a distinction their domains do not have.
 */
merged, domainLo, domainHi, eps = () => 0) {
    // An infinite domain edge is compared exactly: nothing is below -Infinity, and `-Infinity + eps`
    // would be NaN once `eps` scales with magnitude (`k * max(1, |n|)` is Infinity at an infinite n).
    const reachesLo = (m) => domainLo === -Infinity ? m.lo === -Infinity : m.lo <= domainLo + eps(domainLo);
    const reachesHi = (m) => domainHi === Infinity ? m.hi === Infinity : m.hi >= domainHi - eps(domainHi);
    return merged.some(m => reachesLo(m) && reachesHi(m));
}
/**
 * What the merged coverage leaves UNCOVERED across a bounded domain — the values Default Incorrect
 * still catches, and the gap symbols the editor draws.
 *
 * Angle and compass both had this, and they are the same walk: a leading gap, the gaps between
 * consecutive spans, a trailing gap. The differences were not differences. Compass omitted the
 * `cur.lo === prev.hi && both open` case, which cannot arise on a domain with no open bounds, and
 * dropped the inclusive flags it has no use for - so passing closed flags reproduces its behaviour
 * exactly, and it maps the result back onto its own flag-less interval type.
 *
 * A gap's boundary MIRRORS the span beside it: coverage stopping open at 5 leaves a gap closed at 5,
 * because 5 itself is uncovered. That is how a single-point gap arises, and dropping it is what once
 * made `< 5` plus `> 5` read as total coverage.
 *
 * Why the value blocks do not share ONE default-incorrect predicate: angle and compass answer from
 * these gaps, because they need the gaps themselves anyway. `coverageSpansDomain` would answer the
 * same question a second way, and the two disagree at the domain edge, where angle's epsilon
 * forgives coverage that stops a hair short of 360 and a plain comparison does not. One derivation
 * per block matters more here than one derivation across blocks.
 */
export function coverageGaps(merged, domainLo, domainHi, eps) {
    if (merged.length === 0)
        return [{ lo: domainLo, hi: domainHi, loInclusive: true, hiInclusive: true }];
    const gaps = [];
    const first = merged[0];
    if (first.lo > domainLo + eps(domainLo)) {
        gaps.push({ lo: domainLo, hi: first.lo, loInclusive: true, hiInclusive: !first.loInclusive });
    }
    for (let i = 1; i < merged.length; i++) {
        const prev = merged[i - 1];
        const cur = merged[i];
        if (cur.lo > prev.hi || (cur.lo === prev.hi && !prev.hiInclusive && !cur.loInclusive)) {
            gaps.push({ lo: prev.hi, hi: cur.lo, loInclusive: !prev.hiInclusive, hiInclusive: !cur.loInclusive });
        }
    }
    const last = merged[merged.length - 1];
    if (last.hi < domainHi - eps(domainHi)) {
        gaps.push({ lo: last.hi, hi: domainHi, loInclusive: !last.hiInclusive, hiInclusive: true });
    }
    return gaps;
}
/**
 * Whether one span is entirely inside the union of others.
 *
 * A shared endpoint only counts as covered when BOTH sides admit it: a target closed at 5 is not
 * covered by a span that stops open at 5. That single comparison is what the boundary-strictness
 * work came down to.
 */
export function isIntervalFullyCovered(target, coverage, eps) {
    if (coverage.length === 0)
        return false;
    return mergeIntervals(coverage, eps).some(m => (m.lo < target.lo || (m.lo === target.lo && (m.loInclusive || !target.loInclusive)))
        && (target.hi < m.hi || (target.hi === m.hi && (m.hiInclusive || !target.hiInclusive))));
}
/*
 * ── Why compass is NOT here ───────────────────────────────────────────────────────────────────
 *
 * `CompassInterval` carries no inclusive flags, and that is correct for its vocabulary rather than
 * an omission: a compass answer is `exact | range` over a LOOP, so a band with low > high is the
 * arc through the 0/360 seam and every ordered pair of headings names a valid arc. Its merge has no
 * `touchesClosed` test because it has no open bounds to test, and forcing this module's flags onto
 * it would invent a distinction its domain does not have.
 *
 * GPS likewise keeps its own: it measures a DISTANCE from a target, so its intervals are radii and
 * its trigger vocabulary is its own.
 *
 * The census that produced this module is the record of which differences are real. Two blocks
 * differing is not automatically duplication — but three blocks agreeing byte-for-byte is.
 */
//# sourceMappingURL=interval-coverage.js.map